/**
 * career-cv-workbench — the Lebenslauf-WERKBANK (route `/compass/cv`).
 *
 * ONE coherent process: provide material → KI ingests → resolve, all AT the CV
 * (operator direction 2026-07-28). This surface absorbs what used to be scattered
 * across `/onboarding` (upload), `/onboarding/review` (claim review) and
 * `/onboarding/cv` (CV-proposal accept): the person uploads their existing CV +
 * references + certificates, the AI reads them into the model, and every open
 * decision is resolved right here on the document — never by hopping surfaces.
 *
 * The surface renders the person's actual Lebenslauf via the SAME shared renderer the
 * export routes and the print view use (`@career/cv-render-v2` `buildCvRenderModel`),
 * and weaves the resolution band into the document head (§0 seamless document over
 * cards — structure by typography + spacing + hairlines, real containment only for the
 * genuine affordances: the file inputs, the active proposal rows):
 *
 *   - PROVIDE MATERIAL — upload CV / references / certificates / portrait, then start
 *     the analysis. Prominent while the CV is still empty (the common first run);
 *     a quiet disclosure once a CV exists (add more material later).
 *   - ANALYSING — the live ingest run status, push-fed from the Ingest stream.
 *   - RESOLVE — the KI's proposed CV entries (accept / discard, then »Übernehmen«
 *     produces exactly one durable version), the proposed Nachweise (confirm), and
 *     the uploaded documents that can be promoted to typed assets.
 *
 * Belege AT THE TARGET: each durable entry that carries evidence claims discloses them
 * inline — grade, statement, confirm/discard — via the same optimistic EvidenceStore
 * commands the ledger uses. The durable Belegregister (`/compass/evidence`) and the
 * Nachweise library (`/compass/assets`) stay reachable as the all-items views; the ACT
 * of resolving happens here.
 *
 * NO data-model change: everything rides the existing streams (Ingest / Evidence / CV /
 * Asset) and the existing BFF reads + verbs. The decision seam is `status:
 * proposed→confirmed`, surfaced as the »needs-you« indicators.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { buildCvRenderModel, type CvRenderEntry, type CvRenderModel } from "@career/cv-render-v2"
import { nextStep } from "../lib/next-step"
import { forwardDecl, toNextStep } from "../golden-path"
import { GRADE_KEY } from "../lib/claim-card"
import { action, materialTile } from "../lib/action"
import { deriveResolutionCounts, promotableFrom } from "../lib/resolution"
import {
  createCvProposalRejectionStore,
  rejectionsOf,
  selectionFor,
  type CvProposalRejectionStore,
} from "../lib/cv-proposal-draft"
import {
  AssetStoreToken,
  EvidenceStoreToken,
  I18nToken,
  IngestStoreToken,
  PushToken,
  RouterToken,
  SessionToken,
  apiGet,
  apiPost,
  apiUpload,
} from "../services"
import { createStreamClaim, type StreamClaim } from "../lib/push-streams"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { Router } from "@platform/web-kit"
import {
  careerStream,
  ingestRunUrn,
  type AssetStore,
  type AssetView,
  type EvidenceClaimView,
  type EvidenceStore,
  type IngestRunView,
  type IngestStatus,
  type IngestStore,
  type ShellSession,
} from "../domain"

type CvInput = Parameters<typeof buildCvRenderModel>[0]

interface VersionView {
  version: number
  versionedAt: string
}

/** Verbatim attribution of a proposed CV entry to an uploaded document (from `/api/cv/proposal`). */
interface EntrySource {
  documentId: string
  kind: string
  quote: string
}
interface ProposalEntry {
  key: string
  section: string
  title: string
  detail: string
  source: EntrySource | null
  required?: boolean
}

/** An uploaded ingest document not yet promoted to a typed asset. */
interface PromotableDoc {
  ingestId: string
  documentId: string
  kind: string
}

/** The material kinds the werkbank collects (English codes; German labels via t()). */
const MATERIAL_KINDS = ["cv", "reference", "certificate"] as const
type MaterialKind = (typeof MATERIAL_KINDS)[number]
const MATERIAL_LABEL: Record<MaterialKind, CareerKey> = {
  cv: "onboarding.kind.cv",
  reference: "onboarding.kind.reference",
  certificate: "onboarding.kind.certificate",
}

const STATUS_KEY: Record<IngestStatus, CareerKey> = {
  pending: "onboarding.status.pending",
  extracting: "onboarding.status.extracting",
  proposed: "onboarding.status.proposed",
  confirmed: "onboarding.status.confirmed",
  failed: "onboarding.status.failed",
}

/** Render order of the proposal sections — a reader expects a CV to open this way. */
const PROPOSAL_SECTION_ORDER = [
  "basics", "work", "education", "projects", "skills",
  "languages", "certificates", "awards", "publications", "volunteer", "references", "interests",
] as const
const PROPOSAL_SECTION_KEY: Record<string, CareerKey> = {
  basics: "cvProposal.section.basics",
  work: "cvProposal.section.work",
  education: "cvProposal.section.education",
  projects: "cvProposal.section.projects",
  skills: "cvProposal.section.skills",
  languages: "cvProposal.section.languages",
  certificates: "cvProposal.section.certificates",
  awards: "cvProposal.section.awards",
  publications: "cvProposal.section.publications",
  volunteer: "cvProposal.section.volunteer",
  references: "cvProposal.section.references",
  interests: "cvProposal.section.interests",
}
const DOC_KIND_KEY: Record<string, CareerKey> = {
  cv: "cvProposal.doc.cv",
  letter: "cvProposal.doc.letter",
  reference: "cvProposal.doc.reference",
  certificate: "cvProposal.doc.certificate",
  other: "cvProposal.doc.other",
}

const fmtDate = (iso: string): string =>
  new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso))

export class CvWorkbenchVm implements ViewModel {
  readonly model: Signal<CvRenderModel | null> = signal<CvRenderModel | null>(null)
  readonly isDraft: Signal<boolean> = signal(false)
  /** Server-folded per-entry links (`cv.EntryEvidenceLinked`), keyed `${section}:${index}`. */
  readonly links: Signal<Record<string, string[]>> = signal<Record<string, string[]>>({})
  readonly versions: Signal<readonly VersionView[]> = signal<readonly VersionView[]>([])
  readonly claims: ReadonlySignal<readonly EvidenceClaimView[]>
  readonly assets: ReadonlySignal<readonly AssetView[]>
  readonly fixing: Signal<boolean> = signal(false)
  readonly busyPortrait: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)

  // ── Provide material (upload + ingest) ──
  readonly ingestId: Signal<string | null> = signal<string | null>(null)
  readonly busyKind: Signal<string | null> = signal<string | null>(null)
  readonly uploadedCount: Signal<number> = signal(0)
  readonly starting: Signal<boolean> = signal(false)
  readonly provideOpen: Signal<boolean> = signal(false)

  // ── Resolve: CV proposal ──
  readonly proposal: Signal<readonly ProposalEntry[]> = signal<readonly ProposalEntry[]>([])
  readonly proposedAt: Signal<string | null> = signal<string | null>(null)
  readonly selected: Signal<ReadonlySet<string>> = signal<ReadonlySet<string>>(new Set())
  readonly approving: Signal<boolean> = signal(false)
  /** Per-user record of what the person took OUT — see `cv-proposal-draft`. */
  private readonly rejections: CvProposalRejectionStore

  // ── Resolve: Nachweise ──
  readonly promotable: Signal<readonly PromotableDoc[]> = signal<readonly PromotableDoc[]>([])

  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** The per-user CV urn (AX-4: the document is DOM-addressable). */
  readonly urn: Urn

  /** This surface's claim on the shared push channel (`lib/push-streams.ts`): the channel
   *  subscription is the UNION of every mounted surface's claim, never just ours. */
  private readonly claim: StreamClaim

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    private readonly evidence: EvidenceStore,
    private readonly assetStore: AssetStore,
    private readonly ingestStore: IngestStore,
    push: PushChannel,
    private readonly session: ShellSession,
  ) {
    this.t = i18n.t
    this.claim = createStreamClaim(push)
    this.urn = `urn:career:cv:${session.user?.sub ?? "anon"}`
    this.rejections = createCvProposalRejectionStore(session.user?.sub ?? null)
    this.claims = evidence.list("alle")
    this.assets = assetStore.list("alle")
    this.renderDeps = [
      this.model, this.isDraft, this.links, this.versions, this.claims, this.assets, this.fixing,
      this.busyPortrait, this.error, this.loaded, this.ingestId, this.busyKind, this.uploadedCount,
      this.starting, this.provideOpen, this.proposal, this.selected, this.approving, this.promotable,
      evidence.pending, assetStore.pending, this.ingestStore.list("aktiv"),
    ]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.refresh()
    // Belege-at-target: the claim BODIES live on the Evidence stream — subscribe so the
    // inline rows resolve (replay hydrates; live claim.* ticks reconcile confirm/discard).
    const userId = this.userId
    if (userId) this.claim.claim([careerStream(userId, "Evidence")])
    return () => this.claim.release()
  }

  /** The live ingest run (once uploads have opened one) — drives the »Analysiere …« status. */
  get runView(): IngestRunView | undefined {
    const id = this.ingestId.value
    if (!id) return undefined
    return this.ingestStore.entity(ingestRunUrn(id)).value
  }

  private async refresh(): Promise<void> {
    try {
      const [cv, versions, proposal] = await Promise.all([
        apiGet<{ cv: CvInput | null; isDraft: boolean; links: Record<string, string[]> }>("/api/cv"),
        apiGet<{ versions: { version: number; versionedAt: string }[] }>("/api/cv/versions"),
        apiGet<{ entries: ProposalEntry[]; proposedAt: string | null }>("/api/cv/proposal"),
      ])
      this.model.set(cv.cv ? buildCvRenderModel(cv.cv) : null)
      this.isDraft.set(cv.isDraft)
      this.links.set(cv.links ?? {})
      this.versions.set(versions.versions.map(({ version, versionedAt }) => ({ version, versionedAt })))
      this.proposal.set(proposal.entries)
      this.proposedAt.set(proposal.proposedAt)
      // Everything is pre-selected: the proposal is the model's best reading; the user's job is
      // to take things OUT, not to re-enter their CV by hand (cv-proposal law, preserved) —
      // minus whatever this person already took out of THIS proposal, because that same
      // all-selected default would otherwise silently re-accept their rejections on approve.
      this.selected.set(selectionFor(proposal.entries, proposal.proposedAt, this.rejections.load()))
      // With no CV and no proposal, lead the person straight into providing material.
      if (!cv.cv && proposal.entries.length === 0) this.provideOpen.set(true)
      await this.hydrateAssets()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** Hydrate the asset store from the inventory + compute the promotable ingest documents. */
  private async hydrateAssets(): Promise<void> {
    const userId = this.userId
    try {
      const assets = await apiGet<Array<Record<string, unknown>>>("/api/assets")
      if (userId) {
        for (const a of assets) {
          this.assetStore.apply({
            streamId: careerStream(userId, "Asset", String(a["id"] ?? "")),
            version: typeof a["version"] === "number" ? (a["version"] as number) : 1,
            type: "asset.Added",
            payload: { owner: userId, asset: a },
          })
        }
      }
      await this.loadPromotable()
    } catch {
      // The document + proposal above already rendered; a failed asset load leaves that band empty.
    }
  }

  /**
   * Uploaded ingest documents that can be promoted to typed assets, EXCLUDING already-promoted
   * ones. This surface reads the CANDIDATES (every uploaded document); WHICH of them is still
   * promotable is `promotableFrom` in `lib/resolution.ts` — the same rule the shell's sidebar
   * badge applies, so the hero chip and the badge can never disagree by the promotable term.
   */
  private async loadPromotable(): Promise<void> {
    try {
      const runs = await apiGet<{ id: string }[]>("/api/ingest")
      if (!Array.isArray(runs)) return
      const candidates: PromotableDoc[] = []
      for (const run of runs) {
        const ingestId = run.id.replace(/^urn:career:ingest:/, "")
        const detail = await apiGet<{ documents: { id: string; kind: string }[] }>(`/api/ingest/${ingestId}`)
        for (const d of detail.documents ?? []) candidates.push({ ingestId, documentId: d.id, kind: d.kind })
      }
      this.promotable.set(promotableFrom(candidates, this.assets.value))
    } catch {
      this.promotable.set([])
    }
  }

  // ── Provide material ──────────────────────────────────────────────────────

  toggleProvide(): void {
    this.provideOpen.set(!this.provideOpen.value)
  }

  async pickAndUpload(kind: string, portrait = false): Promise<void> {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = portrait ? "image/*" : ".md,.txt,.pdf,.docx"
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) void this.upload(kind, file, portrait)
    }
    input.click()
  }

  private async upload(kind: string, file: File, portrait: boolean): Promise<void> {
    if (portrait) this.busyPortrait.set(true)
    else this.busyKind.set(kind)
    this.error.set(null)
    try {
      const form = new FormData()
      form.set("file", file)
      if (portrait) form.set("portrait", "true")
      else form.set("kind", kind)
      if (!portrait && this.ingestId.value) form.set("ingestId", this.ingestId.value)
      const res = await apiUpload<{ ingestId?: string }>("/api/documents/upload", form)
      if (res.ingestId) this.ingestId.set(res.ingestId)
      if (portrait) await this.refresh()
      else this.uploadedCount.update((n) => n + 1)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      if (portrait) this.busyPortrait.set(false)
      else this.busyKind.set(null)
    }
  }

  async startIngest(): Promise<void> {
    const id = this.ingestId.value
    const userId = this.userId
    if (!id || !userId) return
    this.starting.set(true)
    this.error.set(null)
    try {
      // Subscribe BEFORE starting so replay + live ticks both land, then refresh once the
      // run reaches `proposed` (the surface re-reads to pick up the fresh proposal + claims).
      this.claim.claim([careerStream(userId, "Evidence"), careerStream(userId, "Ingest", ingestRunUrn(id))])
      await apiPost("/api/ingest/start", { ingestId: id })
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.starting.set(false)
    }
  }

  /** The live run reached a terminal state → pull the fresh proposal + assets into view. */
  onRunSettled(): void {
    this.uploadedCount.set(0)
    void this.refresh()
  }

  // ── Resolve: CV proposal ──────────────────────────────────────────────────

  toggleEntry(key: string): void {
    const entry = this.proposal.value.find((e) => e.key === key)
    if (entry?.required) return
    this.selected.update((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    this.saveRejections()
  }

  setAllEntries(on: boolean): void {
    this.selected.set(new Set(on ? this.proposal.value.map((e) => e.key) : this.proposal.value.filter((e) => e.required).map((e) => e.key)))
    this.saveRejections()
  }

  /** Mirror the decisions after every change — an untick exists nowhere else until approve. */
  private saveRejections(): void {
    const proposedAt = this.proposedAt.value
    if (proposedAt === null) return
    const rejected = rejectionsOf(this.proposal.value, this.selected.value)
    if (rejected.length === 0) this.rejections.clear()
    else this.rejections.save({ proposedAt, rejected, updatedAt: new Date().toISOString() })
  }

  async approveProposal(): Promise<void> {
    if (this.approving.value) return
    this.approving.set(true)
    this.error.set(null)
    try {
      await apiPost<{ version: number; accepted: string[]; rejected: string[] }>("/api/cv/proposal/approve", {
        accepted: [...this.selected.value],
      })
      // The decisions are in the durable CV now — the local record has done its job. Cleared
      // BEFORE the refresh so the re-read cannot re-apply them to a fresh proposal.
      this.rejections.clear()
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.approving.set(false)
    }
  }

  // ── Resolve: claims + Nachweise ───────────────────────────────────────────

  /** All claim refs of one entry — the render model's inline refs + the linked fold. */
  entryRefs(sectionId: string, index: number, entry: CvRenderEntry): string[] {
    const linked = this.links.value[`${sectionId}:${index}`] ?? []
    return [...new Set([...entry.claimRefs, ...linked])]
  }

  claimOf(ref: string): EvidenceClaimView | null {
    return this.evidence.entity(ref as Urn).value ?? null
  }

  confirmClaim(claimId: string): void {
    void this.evidence.command({ type: "confirm-claim", payload: { claimId } })
  }
  discardClaim(claimId: string): void {
    void this.evidence.command({ type: "discard-claim", payload: { claimId } })
  }
  confirmAsset(assetId: string): void {
    void this.assetStore.command({ type: "confirm-asset", payload: { assetId } })
  }

  async promote(doc: PromotableDoc): Promise<void> {
    this.error.set(null)
    try {
      await apiPost("/api/assets/promote", { ingestId: doc.ingestId, documentId: doc.documentId, kind: "certificate", title: `Dokument (${doc.kind})` })
      await this.hydrateAssets()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }

  // ── Document acts (preserved) ─────────────────────────────────────────────

  async fixVersion(): Promise<void> {
    this.fixing.set(true)
    this.error.set(null)
    try {
      await apiPost("/api/cv/version")
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.fixing.set(false)
    }
  }

  async pickPortrait(): Promise<void> {
    await this.pickAndUpload("portrait", true)
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  // ── Derived: needs-you ────────────────────────────────────────────────────

  /**
   * Everything still awaiting the person: proposed CV entries + open claims + unconfirmed
   * Nachweise + promotable documents. The arithmetic itself lives in `lib/resolution.ts` and is
   * shared with the shell's sidebar badge — this VM only hands over the four collections it
   * already holds, so the chip and the badge are the SAME number by construction.
   */
  needsCount(): number {
    return deriveResolutionCounts({
      cvProposals: this.proposal.value,
      claims: this.claims.value,
      assets: this.assets.value,
      promotable: this.promotable.value,
    }).total
  }
}

export class CareerCvWorkbench extends KitElement<CvWorkbenchVm> {
  /** Which ingest run we have already reacted to settling (avoids a refresh loop). */
  #settledRun: string | null = null

  protected createViewModel(ctx: KitContext): CvWorkbenchVm {
    return new CvWorkbenchVm(
      ctx.inject(RouterToken),
      ctx.inject(I18nToken),
      ctx.inject(EvidenceStoreToken),
      ctx.inject(AssetStoreToken),
      ctx.inject(IngestStoreToken),
      ctx.inject(PushToken),
      ctx.inject(SessionToken),
    )
  }

  /** After each render: when the live ingest run reaches a terminal state, pull the fresh
   *  proposal + claims + assets in once (the push tick updated the run; this re-reads once). */
  override flushRender(): void {
    super.flushRender()
    const run = this.vm.runView
    const id = this.vm.ingestId.value
    if (!run || !id) return
    const done = run.status === "proposed" || run.status === "confirmed" || run.status === "failed"
    if (done && this.#settledRun !== id) {
      this.#settledRun = id
      this.vm.onRunSettled()
    }
  }

  // ── Werkbank: provide material ──────────────────────────────────────────

  /** One material slot — the shared file-picker tile (§6 `materialTile`), not a local box. */
  private materialCard(vm: CvWorkbenchVm, kind: MaterialKind): TemplateResult {
    const busy = vm.busyKind.value === kind
    return materialTile({
      act: "upload-material",
      label: vm.t(MATERIAL_LABEL[kind]),
      hint: busy ? vm.t("onboarding.uploading") : vm.t("werkbank.material.pick"),
      payload: { kind },
      // While ANY slot uploads every tile is unavailable (one picker at a time), but only the
      // slot actually in flight wears the pending state — the person sees WHICH one is busy.
      disabled: vm.busyKind.value !== null,
      pending: busy,
    })
  }

  private provideBand(vm: CvWorkbenchVm): TemplateResult {
    const open = vm.provideOpen.value
    const uploaded = vm.uploadedCount.value
    const canStart = uploaded > 0 && !vm.starting.value
    const hasCv = vm.model.value !== null
    return html`
      <section class="wb-band wb-provide" aria-labelledby="wb-provide-title">
        <button class="wb-band-head" data-act="toggle-provide" aria-expanded=${open ? "true" : "false"} lang="de">
          <span class="wb-band-eyebrow eyebrow" lang="de">${vm.t("werkbank.provide.eyebrow")}</span>
          <span id="wb-provide-title" class="wb-band-title" lang="de">${hasCv ? vm.t("werkbank.provide.titleMore") : vm.t("werkbank.provide.title")}</span>
          <svg class="wb-band-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </button>
        ${open
          ? html`
              <div class="wb-provide-body">
                <p class="wb-band-lead" lang="de">${vm.t("werkbank.provide.lead")}</p>
                <div class="act-materials">${MATERIAL_KINDS.map((k) => this.materialCard(vm, k))}</div>
                <p class="wb-format-hint" lang="de">${vm.t("onboarding.formatHint")}</p>
                <div class="wb-provide-actions">
                  ${action({
                    act: "start-ingest",
                    label: vm.t("werkbank.provide.start"),
                    pendingLabel: vm.t("onboarding.starting"),
                    variant: "primary",
                    disabled: !canStart,
                    pending: vm.starting.value,
                  })}
                  ${uploaded > 0
                    ? html`<span class="wb-uploaded-count chip" lang="de">${vm.t("onboarding.uploadedN", { n: uploaded })}</span>`
                    : nothing}
                </div>
              </div>
            `
          : nothing}
      </section>
    `
  }

  /** The live ingest status while a run is in flight (aria-live). */
  private analysingBand(vm: CvWorkbenchVm): TemplateResult | typeof nothing {
    const run = vm.runView
    if (!run) return nothing
    const inFlight = run.status === "pending" || run.status === "extracting"
    if (!inFlight) return nothing
    return html`
      <section class="wb-band wb-analysing" aria-live="polite" aria-label=${vm.t("onboarding.runLabel")}>
        <span class="wb-spinner" aria-hidden="true"></span>
        <span class="wb-analysing-text" lang="de">${vm.t(STATUS_KEY[run.status])}</span>
        <span class="chip" lang="de">${vm.t("onboarding.count.docs", { n: run.documents.length })}</span>
      </section>
    `
  }

  // ── Werkbank: resolve the CV proposal ────────────────────────────────────

  private proposalProvenance(vm: CvWorkbenchVm, entry: ProposalEntry): TemplateResult {
    if (!entry.source) {
      return html`<span class="cv-prop-src is-derived" lang="de">${vm.t("cvProposal.derived")}</span>`
    }
    const kind = vm.t(DOC_KIND_KEY[entry.source.kind] ?? "cvProposal.doc.other")
    return html`
      <details class="cv-prop-src">
        <summary lang="de">${vm.t("cvProposal.source", { kind })}</summary>
        <p class="cv-prop-quote-label" lang="de">${vm.t("cvProposal.sourceQuote")}</p>
        <q class="cv-prop-quote">${entry.source.quote}</q>
      </details>
    `
  }

  private proposalRow(vm: CvWorkbenchVm, entry: ProposalEntry): TemplateResult {
    const on = vm.selected.value.has(entry.key)
    const titleId = `wbp-${entry.key.replace(/[^a-zA-Z0-9]/g, "-")}`
    return html`
      <li class=${classMap({ "cv-prop-row": true, "is-out": !on, "is-required": Boolean(entry.required) })}>
        <button
          class="cv-prop-check"
          role="checkbox"
          aria-checked=${on ? "true" : "false"}
          aria-labelledby=${titleId}
          ?disabled=${Boolean(entry.required)}
          data-act="toggle-entry"
          data-key=${entry.key}
        >${on ? "✓" : "○"}</button>
        <span class="cv-prop-body">
          <span class="cv-prop-title" id=${titleId} lang="de">${entry.title}</span>
          ${entry.detail ? html`<span class="cv-prop-detail" lang="de">${entry.detail}</span>` : nothing}
        </span>
        <span class="cv-prop-meta">
          ${entry.required ? html`<span class="cv-prop-src is-required" lang="de">${vm.t("cvProposal.required")}</span>` : this.proposalProvenance(vm, entry)}
        </span>
      </li>
    `
  }

  private proposalBand(vm: CvWorkbenchVm): TemplateResult | typeof nothing {
    const entries = vm.proposal.value
    if (entries.length === 0) return nothing
    const selected = vm.selected.value
    const n = selected.size
    const date = vm.proposedAt.value ? new Date(vm.proposedAt.value).toLocaleDateString("de-DE") : "—"
    const sections = PROPOSAL_SECTION_ORDER
      .map((name) => ({ name, rows: entries.filter((e) => e.section === name) }))
      .filter((s) => s.rows.length > 0)
    return html`
      <section class="wb-band wb-resolve" aria-labelledby="wb-resolve-title">
        <header class="wb-band-titleblock">
          <span class="wb-band-eyebrow eyebrow" lang="de">${vm.t("werkbank.resolve.eyebrow")}</span>
          <h2 id="wb-resolve-title" class="wb-band-title" lang="de">${vm.t("werkbank.resolve.title")}</h2>
          <p class="wb-band-lead" lang="de">${vm.t("werkbank.resolve.lead")}</p>
          <p class="wb-band-meta" lang="de">${vm.t("cvProposal.meta", { selected: n, total: entries.length, date })}</p>
        </header>
        <div class="cv-prop-bulk">
          <button class="btn-secondary" data-act="select-all" lang="de">${vm.t("cvProposal.selectAll")}</button>
          <button class="btn-secondary" data-act="select-none" lang="de">${vm.t("cvProposal.selectNone")}</button>
        </div>
        ${sections.map(
          (s) => html`
            <section class="cv-prop-section" aria-labelledby=${`wbp-sec-${s.name}`}>
              <h3 id=${`wbp-sec-${s.name}`} class="cv-prop-section-title" lang="de">${vm.t(PROPOSAL_SECTION_KEY[s.name] ?? "cvProposal.section.basics")}</h3>
              <ul class="cv-prop-rows" role="list">${repeat(s.rows, (e) => e.key, (e) => this.proposalRow(vm, e))}</ul>
            </section>
          `,
        )}
        <div class="wb-resolve-actions">
          <button class=${classMap({ "btn-primary": true, "is-pending": vm.approving.value })} data-act="approve-proposal" ?disabled=${vm.approving.value} lang="de">
            ${vm.approving.value ? vm.t("cvProposal.approving") : n === 1 ? vm.t("cvProposal.approveOne") : vm.t("cvProposal.approve", { n })}
          </button>
        </div>
      </section>
    `
  }

  // ── Werkbank: resolve the Nachweise (refs/certs) ─────────────────────────

  private nachweiseBand(vm: CvWorkbenchVm): TemplateResult | typeof nothing {
    const proposed = vm.assets.value.filter((a) => a.status === "proposed")
    const promotable = vm.promotable.value
    if (proposed.length === 0 && promotable.length === 0) return nothing
    return html`
      <section class="wb-band wb-nachweise" aria-labelledby="wb-nachweise-title">
        <header class="wb-band-titleblock">
          <span class="wb-band-eyebrow eyebrow" lang="de">${vm.t("werkbank.nachweise.eyebrow")}</span>
          <h2 id="wb-nachweise-title" class="wb-band-title" lang="de">${vm.t("werkbank.nachweise.title")}</h2>
          <p class="wb-band-lead" lang="de">${vm.t("werkbank.nachweise.lead")}</p>
        </header>
        <ul class="wb-nachweis-list" role="list">
          ${repeat(proposed, (a) => a.urn, (a) => html`
            <li class="wb-nachweis-row is-proposed" urn=${a.urn}>
              <span class="wb-nachweis-title" lang="de">${a.title}</span>
              ${a.issuer ? html`<span class="chip" lang="de">${a.issuer}</span>` : nothing}
              <button class="btn-secondary wb-nachweis-act" data-act="confirm-asset" data-asset-id=${a.id} lang="de">${vm.t("asset.confirm")}</button>
            </li>
          `)}
          ${repeat(promotable, (d) => d.documentId, (d) => html`
            <li class="wb-nachweis-row is-promotable" urn=${`urn:career:document:${d.documentId.replace(/^urn:career:document:/, "")}`}>
              <span class="wb-nachweis-title" lang="de">${vm.t("werkbank.nachweise.uploaded", { kind: d.kind })}</span>
              <button class="btn-secondary wb-nachweis-act" data-act="promote-document" data-ingest-id=${d.ingestId} data-document-id=${d.documentId} data-kind=${d.kind} lang="de">${vm.t("asset.promote.action")}</button>
            </li>
          `)}
        </ul>
        <a class="cvdoc-belege-ledger" href="/compass/assets" data-act="nav" data-url="/compass/assets" lang="de">${vm.t("werkbank.nachweise.toLibrary")}</a>
      </section>
    `
  }

  // ── Belege at the target: inline claim rows under the durable entry ──────

  private claimRow(vm: CvWorkbenchVm, claim: EvidenceClaimView): TemplateResult {
    const open = claim.status === "proposed"
    return html`
      <li class=${classMap({ "cvdoc-claim": true, [`is-${claim.grade}`]: true, [`is-${claim.status}`]: true })} urn=${claim.urn}>
        <span class="cvdoc-claim-grade" lang="de">${vm.t(GRADE_KEY[claim.grade])}</span>
        <p class="cvdoc-claim-statement" lang="de">${claim.statement}</p>
        <span class="cvdoc-claim-acts">
          ${open
            ? html`<button class="cvdoc-claim-act cvdoc-act-confirm" data-act="confirm-claim" data-claim-id=${claim.id} lang="de">${vm.t("review.confirm")}</button>
                <button class="cvdoc-claim-act cvdoc-act-discard" data-act="discard-claim" data-claim-id=${claim.id} lang="de">${vm.t("review.discard")}</button>`
            : html`<span class="cvdoc-claim-confirmed" lang="de">${vm.t("review.confirmed")}</span>`}
        </span>
      </li>
    `
  }

  private belege(vm: CvWorkbenchVm, refs: string[]): TemplateResult | typeof nothing {
    if (refs.length === 0) return nothing
    const resolved = refs.map((r) => vm.claimOf(r)).filter((c): c is EvidenceClaimView => c !== null)
    const openCount = resolved.filter((c) => c.status === "proposed").length
    if (resolved.length === 0) {
      return html`<a class="cvdoc-belege-link" href="/compass/evidence" data-act="nav" data-url="/compass/evidence" lang="de">${vm.t("cv.belege.count", { n: refs.length })}</a>`
    }
    return html`
      <details class="cvdoc-belege" ?open=${openCount > 0}>
        <summary lang="de">
          ${vm.t("cv.belege.count", { n: refs.length })}${openCount > 0 ? html` · <span class="cvdoc-belege-open">${vm.t("cv.belege.openN", { n: openCount })}</span>` : nothing}
        </summary>
        <ul class="cvdoc-claims" role="list">${repeat(resolved, (c) => c.urn, (c) => this.claimRow(vm, c))}</ul>
        <a class="cvdoc-belege-ledger" href="/compass/evidence" data-act="nav" data-url="/compass/evidence" lang="de">${vm.t("cv.belege.toLedger")}</a>
      </details>
    `
  }

  // ── The durable document — one shared render model, editorial hairline entries ──

  private static readonly INLINE_LINES = new Set(["skills", "languages", "interests"])

  private entry(vm: CvWorkbenchVm, sectionId: string, e: CvRenderEntry, index: number): TemplateResult {
    const refs = vm.entryRefs(sectionId, index, e)
    const inline = CareerCvWorkbench.INLINE_LINES.has(sectionId)
    return html`
      <article class="cvdoc-entry" urn=${`urn:career:cventry:${sectionId}:${index}`}>
        <div class="cvdoc-entry-head">
          <h3 class="cvdoc-entry-title" lang="de">${e.title}${e.subtitle ? html`<span class="cvdoc-entry-sub"> — ${e.subtitle}</span>` : nothing}</h3>
          ${e.meta ? html`<span class="cvdoc-entry-meta" lang="de">${e.meta}</span>` : nothing}
        </div>
        ${e.lines.length === 0
          ? nothing
          : inline
            ? html`<p class="cvdoc-entry-inline" lang="de">${e.lines.join(" · ")}</p>`
            : html`<ul class="cvdoc-entry-lines" role="list">${e.lines.map((l) => html`<li lang="de">${l}</li>`)}</ul>`}
        ${this.belege(vm, refs)}
      </article>
    `
  }

  /** True when any durable entry in the section carries an open (proposed) claim → a needs-you mark. */
  private sectionHasOpen(vm: CvWorkbenchVm, s: CvRenderModel["sections"][number]): boolean {
    return s.entries.some((e, i) =>
      vm.entryRefs(s.id, i, e).map((r) => vm.claimOf(r)).some((c) => c?.status === "proposed"),
    )
  }

  private section(vm: CvWorkbenchVm, s: CvRenderModel["sections"][number]): TemplateResult {
    const needsYou = this.sectionHasOpen(vm, s)
    return html`
      <section class="ws-section cvdoc-section" aria-labelledby=${`cvdoc-sec-${s.id}`}>
        <header class="ws-sec-head">
          <h2 id=${`cvdoc-sec-${s.id}`} class="ws-sec-label" lang="de">${s.title}</h2>
          ${needsYou ? html`<span class="wb-needs-you" title=${vm.t("werkbank.needsYou.section")} lang="de">${vm.t("werkbank.needsYou.badge")}</span>` : nothing}
          <span class="ws-sec-count">${s.entries.length}</span>
        </header>
        ${repeat(s.entries, (_e, i) => `${s.id}:${i}`, (e, i) => this.entry(vm, s.id, e, i))}
      </section>
    `
  }

  private docHead(vm: CvWorkbenchVm, m: CvRenderModel): TemplateResult {
    const draft = vm.isDraft.value
    const latest = vm.versions.value.at(-1)
    const contact = [m.contact.email, m.contact.phone, m.contact.location, m.contact.url].filter(Boolean)
    const needs = vm.needsCount()
    return html`
      <header class="kb-doc-head cvdoc-head">
        <p class="eyebrow" lang="de">${vm.t("cv.doc.eyebrow")}</p>
        <h1 id="cv-title" class="surface-title kb-doc-title" lang="de">${m.name}</h1>
        ${m.label ? html`<p class="surface-lead cvdoc-label" lang="de">${m.label}</p>` : nothing}
        ${contact.length > 0 ? html`<p class="cvdoc-contact" lang="de">${contact.join(" · ")}</p>` : nothing}
        <p class="cvdoc-state" lang="de">
          ${draft
            ? html`<span class="chip is-draft">${vm.t("cv.draft.notExportable")}</span>`
            : latest
              ? html`<span class="chip is-confirmed">${vm.t("cv.doc.version", { n: latest.version })}</span>`
              : nothing}
          ${needs > 0 ? html`<span class="chip wb-needs-chip">${vm.t("werkbank.needsYou.count", { n: needs })}</span>` : nothing}
        </p>
        <div class="cvdoc-acts">
          ${draft
            ? html`<button class=${classMap({ "btn-primary": true, "is-pending": vm.fixing.value })} data-act="fix-version" ?disabled=${vm.fixing.value} lang="de">
                ${vm.fixing.value ? vm.t("cv.fixing") : vm.t("cv.fixVersion")}
              </button>`
            : nothing}
          ${action({
            act: "upload-portrait",
            label: m.image ? vm.t("cv.portrait.replace") : vm.t("cv.portrait.add"),
            variant: "quiet",
            icon: "camera",
            pending: vm.busyPortrait.value,
          })}
        </div>
      </header>
    `
  }

  private versionRows(vm: CvWorkbenchVm): TemplateResult {
    const versions = vm.versions.value
    return html`
      <section class="ws-section cvdoc-versions" aria-labelledby="cv-versions-title">
        <header class="ws-sec-head">
          <h2 id="cv-versions-title" class="ws-sec-label" lang="de">${vm.t("cv.versions.title")}</h2>
          ${versions.length > 0 ? html`<span class="ws-sec-count">${versions.length}</span>` : nothing}
        </header>
        ${versions.length === 0
          ? html`<p class="surface-empty" lang="de">${vm.t("cv.versions.empty")}</p>`
          : html`<ol class="cvdoc-vlist" role="list">
              ${repeat([...versions].reverse(), (v) => v.version, (v) => html`
                <li class="cvdoc-vrow" urn=${`urn:career:cv:v${v.version}`}>
                  <span class="cvdoc-vlabel" lang="de">${vm.t("cv.versions.label", { n: v.version })}</span>
                  <span class="cvdoc-vdate" lang="de">${fmtDate(v.versionedAt)}</span>
                  <span class="cvdoc-vacts">
                    <a class="cvdoc-vact" href=${`/api/cv/versions/${v.version}/export.md`} download lang="de">${vm.t("cv.export.md")}</a>
                    <a class="cvdoc-vact" href=${`/api/cv/versions/${v.version}/export.jsonld`} download lang="de">${vm.t("cv.export.jsonld")}</a>
                    <a class="cvdoc-vact cvdoc-vact-print" href=${`/cv-print?version=${v.version}`} target="_blank" rel="noopener" lang="de">${vm.t("cv.print")}</a>
                  </span>
                </li>
              `)}
            </ol>`}
      </section>
    `
  }

  /** The empty-CV lead: the werkbank leads with providing material rather than a dead end. */
  private emptyHead(vm: CvWorkbenchVm): TemplateResult {
    return html`
      <header class="kb-doc-head">
        <p class="eyebrow" lang="de">${vm.t("cv.doc.eyebrow")}</p>
        <h1 id="cv-title" class="surface-title kb-doc-title" lang="de">${vm.t("surface.lebenslauf.title")}</h1>
        ${vm.loaded.value ? html`<p class="surface-lead" lang="de">${vm.t("werkbank.empty.lead")}</p>` : nothing}
      </header>
    `
  }

  protected render(vm: CvWorkbenchVm): TemplateResult {
    const m = vm.model.value
    const err = vm.error.value
    return html`
      <section class="surface cv-workbench werkbank" urn=${vm.urn} aria-labelledby="cv-title">
        <div class="ws-doc cvdoc">
          ${m ? this.docHead(vm, m) : this.emptyHead(vm)}
          ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

          ${this.provideBand(vm)}
          ${this.analysingBand(vm)}
          ${this.proposalBand(vm)}
          ${this.nachweiseBand(vm)}

          ${m?.summary ? html`<p class="cvdoc-summary ws-prose" lang="de">${m.summary}</p>` : nothing}
          ${m ? m.sections.map((s) => this.section(vm, s)) : nothing}

          ${m ? this.versionRows(vm) : nothing}
          ${m ? this.goldenNext(vm) : nothing}
        </div>
      </section>
    `
  }

  /** The golden-path branch forward (D4): from the CV onward. */
  private goldenNext(vm: CvWorkbenchVm): TemplateResult | typeof nothing {
    const decl = forwardDecl("career-cv-workbench")
    if (!decl) return nothing
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey)))
  }

  protected override actions(vm: CvWorkbenchVm): ActionMap {
    return {
      "toggle-provide": () => vm.toggleProvide(),
      "upload-material": ({ kind }) => void vm.pickAndUpload(kind ?? "cv", false),
      "start-ingest": () => void vm.startIngest(),
      "toggle-entry": ({ key }) => { if (key) vm.toggleEntry(key) },
      "select-all": () => vm.setAllEntries(true),
      "select-none": () => vm.setAllEntries(false),
      "approve-proposal": () => void vm.approveProposal(),
      "confirm-asset": ({ assetId }) => { if (assetId) vm.confirmAsset(assetId) },
      "promote-document": ({ ingestId, documentId, kind }) => {
        if (ingestId && documentId) void vm.promote({ ingestId, documentId, kind: kind ?? "" })
      },
      "fix-version": () => void vm.fixVersion(),
      "upload-portrait": () => void vm.pickPortrait(),
      "confirm-claim": ({ claimId }) => { if (claimId) vm.confirmClaim(claimId) },
      "discard-claim": ({ claimId }) => { if (claimId) vm.discardClaim(claimId) },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
